import React from 'react';
import styled from '@emotion/styled';
import { Content } from '../../../../apiService';
import { EllipsisText, Flex, Icon } from '../../../../styles';
import { IconPlay, IconVolumeOff, IconVolumeUp } from '../../../../icons';
import HlsPlayer from '../WidgetsPlayer/LazyHlsPlayer';
import { ControlButton } from '../WidgetsPlayer/UI';
import { PlayerChromeApi } from '../WidgetsPlayer/VideoPlayerTypes';
import { VideoTrack } from '../../../Player/PlayerModels';

export default function LivePromotedCardVideoPreview({ content, title, teaser, active = false, onEnded }: {
  content: Content;
  // Conductor title (the live channel's title); falls back to the content title.
  title?: string;
  teaser: VideoTrack | undefined;
  onHoverChange?: (id: string | null) => void;
  active?: boolean;
  onEnded?: () => void;
}) {
  if (!teaser?.src) return null;

  return (
    <Outer data-active="true">
      <HlsPlayer
        source={teaser}
        active={active}
        live
        playLimit={20}
        autoplay
        onEnded={onEnded}
      >
        {(api) => <PreviewControls content={content} title={title} api={api} />}
      </HlsPlayer>
    </Outer>
  );
}

function PreviewControls({ content, title, api }: { content: Content; title?: string; api: PlayerChromeApi }) {
  return (
    <Overlay>
      <Flex align="center" justify="space-between" mt="auto" fullWidth>
        <ContentInfo data-preview-content-info="true">
          <Title variant="body1" as="h2" ml={1}>
            {title || content.title}
          </Title>
        </ContentInfo>
        <ControlsWrapper data-player-controls-wrapper="true" data-player-controls="true">
          <ControlButton type="button" aria-label="Play">
            <Icon size="semiSmall" cursor="pointer" ml="0.4em">
              <IconPlay />
            </Icon>
          </ControlButton>
          <ControlButton
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              e.currentTarget.blur();
              api.toggleMute();
            }}
            aria-label={api.muted ? 'Unmute' : 'Mute'}
          >
            {api.muted ? <IconVolumeOff /> : <IconVolumeUp />}
          </ControlButton>
        </ControlsWrapper>
      </Flex>
    </Overlay>
  );
}

const Outer = styled.div(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  borderRadius: theme.radius.medium,
  zIndex: 1,
  '&[data-active="true"]': {
    zIndex: 9999,
  },
  '&[data-active="true"] video': {
    opacity: 1,
  },
  '&[data-active="true"] [data-player-controls="true"]': {
    opacity: 1,
    pointerEvents: 'auto',
  },
}));

const Overlay = styled.div(({ theme }) => ({
  position: 'relative',
  zIndex: 2,
  padding: theme.spacing(2),
  display: 'flex',
  height: '100%',
  width: '100%',
}));

const ControlsWrapper = styled(Flex)(({ theme }) => ({
  marginRight: theme.spacing(1),
  zIndex: 2,
  gap: '8px',
}));

// Let the title block take the space left of the controls, so the title can ellipsis-truncate.
const ContentInfo = styled(Flex)({
  flex: 1,
  minWidth: 0,
});

const Title = styled(EllipsisText)(({ theme }) => ({
  fontWeight: theme.font.weight.demibold,
  ...theme.font.responsiveSize(1),
  minWidth: 0,
}));
